import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  AddBusiness,
  Link as LinkIcon,
  PostAdd,
} from '@mui/icons-material';
import { CLIENT_SCOPED_VARIANTS } from '../utils/clientReadinessGate';
import { getNexusTokens } from '../theme/tokens';

const VARIANT_CONFIG = {
  [CLIENT_SCOPED_VARIANTS.NO_CLIENTS]: {
    icon: AddBusiness,
    title: 'Create a client workspace',
    description:
      'Nexus is organized around clients (workspaces). Add your first client to connect platforms, upload media, and publish posts.',
    primaryLabel: 'Create a client',
    primaryTo: '/clients',
  },
  [CLIENT_SCOPED_VARIANTS.NO_PLATFORM]: {
    icon: LinkIcon,
    title: 'Connect a platform or Search Console',
    description:
      'Connect a publishing platform and/or Google Search Console for this client. Rankings and GSC insights do not require social posts — connections are managed on the Clients page.',
    primaryLabel: 'Open Clients',
    primaryTo: (clientId) =>
      clientId ? `/clients?client_id=${clientId}` : '/clients',
  },
  [CLIENT_SCOPED_VARIANTS.NO_POSTS]: {
    icon: PostAdd,
    title: 'Create your first post',
    description:
      'You have a connected platform. Draft or schedule a post to unlock social analytics here. Rankings and Search Console stay available without posts once GSC/CSE is configured.',
    primaryLabel: 'Create a post',
    primaryTo: '/posts',
    secondaryLabel: 'Open Scheduling',
    secondaryTo: '/scheduling',
  },
};

/**
 * Honest empty state for client-scoped pages (Phase 3.2).
 * variant: no_clients | no_platform | no_posts
 */
export default function ClientScopedEmptyState({
  variant,
  clientId = null,
  postsPrimaryTo = '/posts',
  sx,
}) {
  const theme = useTheme();
  const tokens = getNexusTokens(theme);
  const config = VARIANT_CONFIG[variant];
  if (!config) return null;

  const Icon = config.icon;
  let primaryTo = config.primaryTo;
  if (typeof primaryTo === 'function') {
    primaryTo = primaryTo(clientId);
  }
  if (variant === CLIENT_SCOPED_VARIANTS.NO_POSTS && postsPrimaryTo) {
    primaryTo = postsPrimaryTo;
  }

  return (
    <Card
      sx={{
        bgcolor: tokens.colors.surface.paper,
        border: `1px solid ${tokens.colors.border.default}`,
        borderRadius: 2,
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${tokens.colors.accent.primary}1F`,
              color: tokens.colors.accent.primary,
              flexShrink: 0,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: tokens.colors.text.primary, fontWeight: 600 }}>
              {config.title}
            </Typography>
            <Typography variant="body2" sx={{ color: tokens.colors.text.secondary, mt: 0.5 }}>
              {config.description}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <Button
            component={RouterLink}
            to={primaryTo}
            variant="contained"
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {config.primaryLabel}
          </Button>
          {config.secondaryTo && (
            <Button
              component={RouterLink}
              to={config.secondaryTo}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              {config.secondaryLabel}
            </Button>
          )}
        </Box>

        {variant === CLIENT_SCOPED_VARIANTS.NO_PLATFORM && clientId && (
          <Alert severity="info" sx={{ mt: 2 }}>
            Platform connections are managed on the client detail page — the same
            place as Google Search Console and platform OAuth.
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}
